import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { LOCALES } from "@/lib/locale";

export const BLOG_CATEGORIES = [
  { slug: "trauma-recovery", label: "Trauma Recovery" },
  { slug: "cptsd", label: "CPTSD" },
  { slug: "nervous-system", label: "Nervous System" },
  { slug: "grounding", label: "Grounding" },
  { slug: "somatic-therapy", label: "Somatic Therapy" },
  { slug: "sleep", label: "Sleep" },
  { slug: "anxiety", label: "Anxiety" },
  { slug: "relationships", label: "Relationships" },
  { slug: "attachment", label: "Attachment" },
  { slug: "self-care", label: "Self-Care" },
] as const;

export type BlogCategorySlug = (typeof BLOG_CATEGORIES)[number]["slug"];

export function categoryLabel(slug: string): string {
  return BLOG_CATEGORIES.find((c) => c.slug === slug)?.label ?? slug;
}

export type BlogPostSummary = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  cover_image_url: string | null;
  category: BlogCategorySlug;
  tags: string[];
  reading_time_minutes: number;
  published_at: string | null;
  author_name_override: string | null;
  lang: string;
};

export type BlogFaq = { question: string; answer: string };

export type BlogPostFull = BlogPostSummary & {
  body_markdown: string;
  meta_title: string | null;
  meta_description: string | null;
  updated_at: string;
  related_post_ids: string[];
  faqs: BlogFaq[] | null;
};

/**
 * The language the page is being rendered for. src/server.ts puts it on the
 * inner request when a visitor is on /hi/, /de/ and so on. Only ever called
 * inside a server handler, so the server-only import never reaches the browser.
 */
async function requestLang(explicit?: string): Promise<string> {
  if (explicit) return explicit;
  try {
    const { getRequest } = await import("@tanstack/react-start/server");
    return getRequest().headers.get("x-rt-locale") || "en";
  } catch {
    return "en";
  }
}

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

const SUMMARY_COLS =
  "id,slug,title,excerpt,cover_image_url,category,tags,reading_time_minutes,published_at,author_name_override,lang";

export const listPublishedPosts = createServerFn({ method: "GET" })
  .inputValidator(
    (d: { q?: string; category?: string; limit?: number; lang?: string } | undefined) => d ?? {},
  )
  .handler(async ({ data }) => {
    const sb = publicClient();
    let query = sb
      .from("blog_posts")
      .select(SUMMARY_COLS)
      .eq("status", "published")
      // Articles written in a language are listed only on that language's
      // pages — a Hindi article never appears in the English blog.
      .eq("lang", await requestLang(data.lang))
      .order("published_at", { ascending: false })
      .limit(data.limit ?? 100);

    if (data.category && data.category.length > 0) {
      query = query.eq("category", data.category as BlogCategorySlug);
    }
    if (data.q && data.q.trim().length > 0) {
      const term = `%${data.q.trim().replace(/[%_]/g, "")}%`;
      query = query.or(`title.ilike.${term},excerpt.ilike.${term}`);
    }
    const { data: rows, error } = await query;
    if (error) throw new Error(error.message);
    return (rows ?? []) as BlogPostSummary[];
  });

export const getPublishedPostBySlug = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string; lang?: string }) => d)
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { data: post, error } = await sb
      .from("blog_posts")
      .select(
        SUMMARY_COLS +
          ",body_markdown,meta_title,meta_description,updated_at,related_post_ids,faqs",
      )
      .eq("slug", data.slug)
      .eq("status", "published")
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!post) return null;
    // An article written in one language is served only on that language's
    // pages, so an English reader never lands on a Hindi article.
    if ((post as unknown as { lang: string }).lang !== (await requestLang(data.lang))) return null;

    // Related: explicit list first, fall back to same-category latest.
    let related: BlogPostSummary[] = [];
    const p = post as unknown as BlogPostFull;
    if (p.related_post_ids && p.related_post_ids.length > 0) {
      const { data: rel } = await sb
        .from("blog_posts")
        .select(SUMMARY_COLS)
        .in("id", p.related_post_ids)
        .eq("lang", p.lang)
        .eq("status", "published");
      related = (rel ?? []) as BlogPostSummary[];
    }
    if (related.length < 4) {
      const need = 4 - related.length;
      const excludeIds = [p.id, ...related.map((r) => r.id)];
      const { data: more } = await sb
        .from("blog_posts")
        .select(SUMMARY_COLS)
        .eq("status", "published")
        .eq("lang", p.lang)
        .eq("category", p.category)
        .not("id", "in", `(${excludeIds.join(",")})`)
        .order("published_at", { ascending: false })
        .limit(need);
      related = [...related, ...((more ?? []) as BlogPostSummary[])];
    }

    return { post: p, related };
  });

export const listAllPublishedSlugs = createServerFn({ method: "GET" }).handler(async () => {
  const sb = publicClient();
  const { data, error } = await sb
    .from("blog_posts")
    .select("slug,updated_at,lang")
    .eq("status", "published");
  if (error) throw new Error(error.message);
  return (data ?? []) as { slug: string; updated_at: string; lang: string }[];
});

// ---------- Admin ----------

export const adminListAllPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("blog_posts")
      .select("id,slug,title,category,status,published_at,updated_at,lang")
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const adminGetPost = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    const { data: row, error } = await context.supabase
      .from("blog_posts")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return row;
  });

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80);
}

function readingTime(md: string): number {
  const words = md.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 225));
}

type UpsertInput = {
  id?: string | null;
  slug?: string | null;
  title: string;
  excerpt: string;
  body_markdown: string;
  meta_title?: string | null;
  meta_description?: string | null;
  cover_image_url?: string | null;
  category: BlogCategorySlug;
  tags: string[];
  author_name_override?: string | null;
  status: "draft" | "published";
  related_post_ids?: string[];
  /** The language the article is written in; it is only listed on that language's blog. */
  lang?: string;
};

export const adminUpsertPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: UpsertInput) => d)
  .handler(async ({ data, context }) => {
    const slug = (data.slug && data.slug.length > 0 ? data.slug : slugify(data.title));
    const payload = {
      slug,
      title: data.title,
      excerpt: data.excerpt ?? "",
      body_markdown: data.body_markdown ?? "",
      meta_title: data.meta_title || null,
      meta_description: data.meta_description || null,
      cover_image_url: data.cover_image_url || null,
      category: data.category,
      tags: data.tags ?? [],
      author_id: context.userId,
      author_name_override: data.author_name_override || null,
      reading_time_minutes: readingTime(data.body_markdown ?? ""),
      status: data.status,
      published_at:
        data.status === "published" ? new Date().toISOString() : null,
      related_post_ids: data.related_post_ids ?? [],
      // An article belongs to exactly one language and is listed only there.
      lang: LOCALES.includes((data.lang ?? "en") as (typeof LOCALES)[number])
        ? (data.lang ?? "en")
        : "en",
    };

    if (data.id) {
      // Preserve published_at if already set and still published
      const { data: existing } = await context.supabase
        .from("blog_posts")
        .select("published_at,status")
        .eq("id", data.id)
        .maybeSingle();
      if (existing?.published_at && data.status === "published") {
        payload.published_at = existing.published_at;
      }
      const { data: row, error } = await context.supabase
        .from("blog_posts")
        .update(payload)
        .eq("id", data.id)
        .select()
        .single();
      if (error) throw new Error(error.message);
      return row;
    }
    const { data: row, error } = await context.supabase
      .from("blog_posts")
      .insert(payload)
      .select()
      .single();
    if (error) throw new Error(error.message);
    return row;
  });

export const adminDeletePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("blog_posts").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
